/**
 * My QR — one refreshable QR code for this personal account. What happens when it's scanned
 * depends on context (identity / event check-in / table booking) — see backend/src/routes/personal-qr.routes.ts.
 * Token is short-lived (30s, server-set) so this page auto-regenerates it while open.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { qrApi } from '../services/api';

const SHAREABLE_FIELDS: { key: string; label: string }[] = [
  { key: 'firstName', label: 'First name' },
  { key: 'lastName', label: 'Last name' },
  { key: 'username', label: 'Username' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
];

const MyQR: React.FC = () => {
  const [qrImage, setQrImage] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [visibleFields, setVisibleFieldsState] = useState<string[]>([]);
  const [savingFields, setSavingFields] = useState(false);
  const [fieldsError, setFieldsError] = useState<string | null>(null);

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const generate = useCallback(async () => {
    setError(null);
    try {
      const res = await qrApi.generate();
      if (res?.qrImage) {
        setQrImage(res.qrImage);
        setExpiresAt(res.expiresAt);
      } else {
        setError(res?.error || 'Failed to generate QR code');
      }
    } catch {
      setError('Failed to generate QR code');
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-regenerate a couple seconds before the current token actually expires, so the code on
  // screen is (almost) always scannable without the user having to tap anything.
  useEffect(() => {
    generate();
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      if (tickTimerRef.current) clearInterval(tickTimerRef.current);
      // Hide the code the moment this screen closes rather than leaving a still-valid QR
      // sitting scannable in a screenshot after the user navigated away.
      qrApi.invalidate().catch(() => {});
    };
  }, [generate]);

  useEffect(() => {
    if (!expiresAt) return;
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    if (tickTimerRef.current) clearInterval(tickTimerRef.current);

    const msLeft = new Date(expiresAt).getTime() - Date.now();
    setSecondsLeft(Math.max(0, Math.ceil(msLeft / 1000)));

    tickTimerRef.current = setInterval(() => {
      const left = Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 1000));
      setSecondsLeft(left);
    }, 1000);

    refreshTimerRef.current = setTimeout(() => {
      generate();
    }, Math.max(1000, msLeft - 2000));

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      if (tickTimerRef.current) clearInterval(tickTimerRef.current);
    };
  }, [expiresAt, generate]);

  useEffect(() => {
    (async () => {
      try {
        const res = await qrApi.getVisibleFields();
        if (Array.isArray(res?.visibleFields)) setVisibleFieldsState(res.visibleFields);
      } catch {
        // Non-fatal — the QR itself still works, just show the default (empty) selection.
      }
    })();
  }, []);

  const toggleField = (key: string) => {
    setVisibleFieldsState((prev) =>
      prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key]
    );
  };

  const saveFields = async () => {
    setSavingFields(true);
    setFieldsError(null);
    try {
      const res = await qrApi.setVisibleFields(visibleFields);
      if (!Array.isArray(res?.visibleFields)) {
        setFieldsError(res?.error || 'Failed to save sharing preferences');
      }
    } catch {
      setFieldsError('Failed to save sharing preferences');
    } finally {
      setSavingFields(false);
    }
  };

  return (
    <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
      <div
        style={{
          background: 'linear-gradient(135deg, #232526 0%, #414345 100%)',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          color: 'white',
        }}
      >
        <h1 style={{ margin: '0 0 8px 0', fontSize: '24px' }}>🔲 My QR</h1>
        <p style={{ margin: 0, opacity: 0.95, fontSize: '14px' }}>
          One code — share your details, check in to an event, or book a table.
        </p>
      </div>

      <div
        style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '16px',
          border: '1px solid #e2e8f0',
          textAlign: 'center',
        }}
      >
        {loading ? (
          <p>Loading…</p>
        ) : error ? (
          <>
            <p style={{ color: '#dc2626', marginBottom: '12px' }}>{error}</p>
            <button type="button" onClick={generate} style={buttonStyle}>
              Try again
            </button>
          </>
        ) : (
          <>
            {qrImage && (
              <img
                src={qrImage}
                alt="Your personal QR code"
                style={{ width: '240px', height: '240px', maxWidth: '100%' }}
              />
            )}
            <p style={{ marginTop: '12px', fontSize: '13px', color: '#64748b' }}>
              {secondsLeft > 0
                ? `Refreshes automatically in ${secondsLeft}s`
                : 'Refreshing…'}
            </p>
            <button type="button" onClick={generate} style={{ ...buttonStyle, marginTop: '8px' }}>
              Regenerate now
            </button>
          </>
        )}
      </div>

      <div
        style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '16px',
          border: '1px solid #e2e8f0',
        }}
      >
        <h2 style={{ fontSize: '16px', margin: '0 0 4px 0' }}>What people see when they scan you</h2>
        <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 12px 0' }}>
          Only checked fields are shared. Event check-in and table bookings don't need any of these
          turned on — this only affects someone scanning you to get your contact details.
        </p>
        {SHAREABLE_FIELDS.map(({ key, label }) => (
          <label
            key={key}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', fontSize: '14px' }}
          >
            <input
              type="checkbox"
              checked={visibleFields.includes(key)}
              onChange={() => toggleField(key)}
            />
            {label}
          </label>
        ))}
        {fieldsError && (
          <p style={{ color: '#dc2626', fontSize: '13px', marginTop: '8px' }}>{fieldsError}</p>
        )}
        <button
          type="button"
          onClick={saveFields}
          disabled={savingFields}
          style={{ ...buttonStyle, marginTop: '12px', background: '#4caf50' }}
        >
          {savingFields ? 'Saving…' : 'Save sharing preferences'}
        </button>
      </div>
    </div>
  );
};

const buttonStyle: React.CSSProperties = {
  padding: '8px 16px',
  background: '#334155',
  border: 'none',
  borderRadius: '8px',
  color: 'white',
  cursor: 'pointer',
  fontSize: '14px',
};

export default MyQR;
